import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, CaretLeft, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

import {
  useGuidedFlowAdvanceMutation,
  useGuidedFlowQuery,
  useGuidedFlowSkipMutation,
} from '../../features/guidedFlow/queries';
import { useAuth } from '../../store/AuthContext';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import pt from '../../locales/pt';
import {
  apiErrorMessage,
  countCompletedGuidedSteps,
  formatGuidedFlowTemplate,
  filterVisibleGuidedSteps,
  isGuidedFlowFinished,
  resolveGuidedWizardStep,
} from '../../utils/guidedFlowUtils';
import { withGuidedReturn } from '../../lib/guidedFlowNavigation';
import { Button } from '../ui/Button';
import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { useSnapshotsTimelineQuery } from '../../features/snapshots/queries';
import { getGuidedFlowEnrichment } from '../../data/guidedFlowEnrichment';
import { GuidedFlowSelfieDayStep } from './GuidedFlowSelfieDayStep';
import { cn } from '../../utils/cn';

/**
 * Vista da jornada guiada — `ds/journey/*` no .pen. Lista todos os passos em
 * simultâneo (timeline), com um ecrã de celebração à parte quando concluída.
 */
export function GuidedFlowWizardModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [celebrate, setCelebrate] = useState(false);
  const { data: state, isLoading: flowLoading } = useGuidedFlowQuery({
    enabled: open,
    refetchInterval: open && !celebrate ? 4000 : false,
  });
  const advance = useGuidedFlowAdvanceMutation();
  const skip = useGuidedFlowSkipMutation();
  const { profile, refreshMe } = useAuth();
  const { currentCycle } = useSelfieCycle();
  const { data: patrimonioTimeline = [] } = useSnapshotsTimelineQuery({
    enabled: open,
  });

  const hasSnapshots = patrimonioTimeline.length > 0;
  const stepCompleted = state?.step_completed || {};
  const selfieDayOfMonth = profile?.selfie_day_of_month;
  const selfieDayConfigured =
    (profile?.seen_wizards || []).includes('selfie_day_setup') ||
    Boolean(stepCompleted.g1_selfie_day);
  const visibilityOptions = {
    hasSnapshots,
    stepCompleted,
    selfieDayOfMonth,
    selfieDayConfigured,
    hasActiveDebts: profile?.has_active_debts,
    currentCycle,
  };

  const activeStep = state ? resolveGuidedWizardStep(state, visibilityOptions) : null;
  const finished = state ? isGuidedFlowFinished(state, visibilityOptions) : false;

  useEffect(() => {
    if (open && finished) {
      setCelebrate(true);
    }
  }, [open, finished]);

  useEffect(() => {
    if (!open) setCelebrate(false);
  }, [open]);

  if (!open) return null;

  const visibleSteps = state
    ? filterVisibleGuidedSteps(state.order, visibilityOptions)
    : [];
  const totalSteps = visibleSteps.length;
  const skipped = state?.skipped || [];
  const doneCount = countCompletedGuidedSteps(
    state?.order || [],
    stepCompleted,
    { ...visibilityOptions, skipped },
  );
  const copy = pt.dashboard.guidedFlow;
  const g3Progress = state?.step_progress?.g3_first_expenses;

  const handleSkip = () => {
    skip.mutate(undefined, {
      onSuccess: (nextState) => {
        if (nextState?.completed) setCelebrate(true);
      },
      onError: (error) => {
        toast.error(
          apiErrorMessage(error, 'Não foi possível saltar este passo.'),
        );
      },
    });
  };

  const handleSelfieDayContinue = async () => {
    try {
      await advance.mutateAsync();
      if (typeof refreshMe === 'function') await refreshMe();
    } catch (error) {
      toast.error(
        apiErrorMessage(error, 'Não foi possível avançar para o próximo passo.'),
      );
      throw error;
    }
  };

  const handleStartStep = (stepKey) => {
    const stepCopy = copy.steps[stepKey];
    if (stepKey === 'g1_selfie_day') return;
    if (!stepCopy?.ctaPath) return;
    onOpenChange(false);
    navigate(withGuidedReturn(stepCopy.ctaPath));
  };

  const closeCelebration = () => {
    setCelebrate(false);
    onOpenChange(false);
  };

  if (celebrate) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetContent
          fullscreenMobile
          headerAlign="start"
          className="border-0 bg-transparent p-0 shadow-none"
        >
          <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden rounded-none bg-gradient-to-br from-sage-500 to-sage-700 px-space-6 py-space-12 text-center dark:from-sage-600 dark:to-sage-950 md:rounded-ds-xl">
            <div
              aria-hidden
              className="pointer-events-none absolute right-[-70px] top-[-70px] h-[230px] w-[230px] rounded-full bg-on-hero/[0.08] blur-2xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-[-100px] left-[-80px] h-[180px] w-[180px] rounded-full bg-on-hero/[0.06] blur-2xl"
            />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-ds-full bg-sage-50 shadow-lg">
              <Check className="h-[46px] w-[46px] text-sage-700" weight="bold" aria-hidden />
            </div>
            <h2 className="relative mt-space-5 font-display text-type-title-page font-semibold leading-tight text-on-hero">
              {copy.celebration.title}
            </h2>
            <p className="relative mt-space-3 max-w-[290px] text-type-body leading-relaxed text-on-hero-muted">
              {copy.celebration.body}
            </p>
            <div className="relative mt-space-5 flex flex-col items-center gap-0.5 rounded-ds-lg bg-on-hero/20 px-space-5 py-space-3">
              <p className="type-display-metric text-on-hero">{totalSteps || 10}</p>
              <p className="type-label-meta text-on-hero-muted">{copy.celebration.stepsStat}</p>
            </div>
            <Button
              type="button"
              className="relative mt-space-6 w-full rounded-ds-lg border-0 bg-on-hero py-space-4 text-type-body font-bold text-sage-700 hover:bg-on-hero/95"
              onClick={closeCelebration}
            >
              {copy.celebration.cta}
              <ArrowRight className="h-[18px] w-[18px]" weight="bold" aria-hidden />
            </Button>
          </div>
        </BottomSheetContent>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent>
        <div className="sticky top-0 z-10 -mx-space-5 flex flex-col gap-space-3 border-b border-border bg-card px-space-5 pb-space-4">
          <div className="flex h-9 items-center justify-between">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="-ml-1.5 flex h-9 w-9 items-center justify-center rounded-ds-full hover:bg-muted"
              aria-label={pt.common.back}
            >
              <CaretLeft className="h-5 w-5" aria-hidden />
            </button>
            <span className="text-type-body-sm font-semibold text-foreground">
              {copy.journeyTitle}
            </span>
            <span className="h-9 w-9" aria-hidden />
          </div>
          <h2 className="font-display text-type-title-page font-semibold text-foreground">
            {copy.journeyHeadline}
          </h2>
          <div className="flex items-center gap-space-3">
            <div className="flex flex-1 gap-space-1" aria-hidden>
              {visibleSteps.map((stepKey) => (
                <span
                  key={stepKey}
                  className={cn(
                    'h-1.5 flex-1 rounded-ds-full',
                    stepCompleted[stepKey] || skipped.includes(stepKey)
                      ? 'bg-sage-600'
                      : 'bg-border',
                  )}
                />
              ))}
            </div>
            <span className="text-type-label font-bold tabular-nums text-sage-on-tint">
              {doneCount}/{totalSteps}
            </span>
          </div>
        </div>

        <div className="pt-space-4">
          {flowLoading && !state ? (
            <GuidedFlowLoadingSkeleton />
          ) : (
            <ol className="flex flex-col">
              {visibleSteps.map((stepKey, index) => {
                const stepCopy = copy.steps[stepKey];
                if (!stepCopy) return null;
                const done =
                  Boolean(stepCompleted[stepKey]) || skipped.includes(stepKey);
                const isCurrent = activeStep === stepKey && !done;
                const n = index + 1;
                const why = stepCopy.subtitle;
                const enrichment = getGuidedFlowEnrichment(stepKey);
                const isLast = index === visibleSteps.length - 1;

                return (
                  <li key={stepKey} className="flex gap-space-3">
                    <div className="flex w-[30px] shrink-0 flex-col items-center gap-1">
                      {done ? (
                        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-ds-full bg-sage-600 text-on-hero">
                          <Check className="h-[15px] w-[15px]" weight="bold" aria-hidden />
                        </span>
                      ) : isCurrent ? (
                        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-ds-full border-2 border-sage-500 bg-card text-type-label font-bold text-sage-700 shadow-[0_3px_10px] shadow-sage-glow">
                          {n}
                        </span>
                      ) : (
                        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-ds-full border-[1.5px] border-stone-300 bg-muted text-type-label font-bold text-muted-foreground dark:border-stone-600">
                          {n}
                        </span>
                      )}
                      {!isLast ? (
                        <span
                          className={cn(
                            'w-0.5 min-h-[14px] flex-1 rounded-ds-full',
                            done ? 'bg-sage-600' : 'bg-border',
                          )}
                          aria-hidden
                        />
                      ) : null}
                    </div>

                    <div className={cn('min-w-0 flex-1', !isLast && 'pb-space-5')}>
                      {isCurrent ? (
                        <div className="flex flex-col gap-space-2 rounded-ds-lg border border-sage-500 bg-sage-tint p-space-4 shadow-[0_8px_20px] shadow-sage-glow-soft">
                          <p className="type-section text-sage-on-tint tracking-[0.08em]">
                            {copy.youAreHere}
                          </p>
                          <p className="font-display text-type-title-card font-semibold leading-snug text-foreground">
                            {stepCopy.title}
                          </p>
                          <p className="text-type-body-sm leading-relaxed text-muted-foreground">
                            {enrichment?.lead || why}
                          </p>
                          {stepKey === 'g3_first_expenses' && g3Progress ? (
                            <p className="text-type-label font-semibold text-foreground">
                              {formatGuidedFlowTemplate(
                                copy.steps.g3_first_expenses.progressLabel,
                                {
                                  count: g3Progress.current ?? 0,
                                  required: g3Progress.required ?? 3,
                                },
                              )}
                            </p>
                          ) : null}

                          {stepKey === 'g1_selfie_day' ? (
                            <div className="pt-space-2">
                              <GuidedFlowSelfieDayStep
                                initialDay={selfieDayOfMonth}
                                continueLabel={stepCopy.cta}
                                onContinue={handleSelfieDayContinue}
                                isAdvancing={advance.isPending}
                              />
                              <button
                                type="button"
                                onClick={handleSkip}
                                disabled={skip.isPending}
                                className="mt-space-2 w-full text-center text-type-body-sm font-semibold text-muted-foreground hover:underline"
                              >
                                {copy.skipButton}
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-space-2 pt-space-2">
                              <Button
                                type="button"
                                className="w-full rounded-ds-md text-type-body-sm font-bold"
                                onClick={() => handleStartStep(stepKey)}
                              >
                                {copy.startStepButton}
                                <ArrowRight className="h-[18px] w-[18px]" weight="bold" aria-hidden />
                              </Button>
                              <button
                                type="button"
                                onClick={handleSkip}
                                disabled={skip.isPending}
                                className="w-full text-center text-type-body-sm font-semibold text-muted-foreground hover:underline disabled:opacity-50"
                              >
                                {skip.isPending ? pt.common.loading : copy.skipButton}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : done ? (
                        <div className="pt-0.5">
                          <div className="flex flex-wrap items-center gap-space-2">
                            <span className="font-display text-type-title-card font-semibold text-foreground">
                              {stepCopy.title}
                            </span>
                            <span className="type-chip rounded-ds-full bg-sage-tint px-space-2 py-0.5 text-sage-on-tint">
                              {copy.doneTag}
                            </span>
                          </div>
                          <p className="mt-1 text-type-body-sm leading-relaxed text-muted-foreground">
                            {why}
                          </p>
                        </div>
                      ) : (
                        <div className="pt-0.5 opacity-[0.65]">
                          <span className="font-display text-type-title-card font-semibold text-muted-foreground">
                            {stepCopy.title}
                          </span>
                          <p className="mt-1 text-type-body-sm leading-relaxed text-muted-foreground">
                            {why}
                          </p>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}

function GuidedFlowLoadingSkeleton() {
  const widths = [210, 180, 230];
  return (
    <div className="flex flex-col gap-space-5" aria-hidden>
      {widths.map((w, i) => (
        <div key={i} className="flex gap-space-3">
          <div className="h-[30px] w-[30px] shrink-0 rounded-ds-full bg-skeleton" />
          <div className="flex flex-1 flex-col gap-space-2 pt-1">
            <div className="h-3 rounded-ds-full bg-skeleton" style={{ width: `${w}px` }} />
            <div className="h-[9px] w-full rounded-ds-full bg-skeleton" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default GuidedFlowWizardModal;
